import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation, type TFunction } from "react-i18next";
import {
  OFF_LABEL_COUNTRIES,
  OFF_LABEL_KINDS,
  type OffLabelCountry,
  type OffLabelKind,
} from "../../../../../../api/src/shared/domain/off-label";
import type { ArticlePageArticle } from "../../hooks/useArticleData";

type OffLabelWarning = ArticlePageArticle["offLabelWarnings"][number];

// A switch and not a lookup: `pnpm i18n:scan` only sees literal keys, so a
// phrase resolved through a table would be pruned from all eight locales. The
// `never` check makes a country added to `OFF_LABEL_COUNTRIES` a type error
// here until its phrase exists. The phrase is a key per country because the
// French preposition changes with the country ("en France", "au Portugal").
function countryPhrase(t: TFunction, country: OffLabelCountry): string {
  switch (country) {
    case "FR":
      return t("in France");
    default:
      throw new Error(country satisfies never);
  }
}

// One sentence per country and kind, which lists its drugs: ten drugs used off
// label in France make one sentence, not ten. The order follows the country and
// kind lists, then the order of the api.
function groupWarnings(warnings: readonly OffLabelWarning[]): {
  country: OffLabelCountry;
  kind: OffLabelKind;
  inns: string[];
}[] {
  return OFF_LABEL_COUNTRIES.flatMap((country) =>
    OFF_LABEL_KINDS.map((kind) => ({
      country,
      kind,
      inns: warnings
        .filter(
          (warning) => warning.country === country && warning.kind === kind
        )
        .map((warning) => warning.inn),
    }))
  ).filter((group) => group.inns.length > 0);
}

function warningText(
  t: TFunction,
  listFormat: Intl.ListFormat,
  group: ReturnType<typeof groupWarnings>[number]
): string {
  const phrase = countryPhrase(t, group.country);
  const inns = listFormat.format(group.inns);
  const several = group.inns.length > 1;
  const { kind } = group;
  switch (kind) {
    case "off_label":
      return several
        ? t(
            "This article discusses uses of {{inns}} outside their authorised conditions of use {{countryPhrase}}.",
            { inns, countryPhrase: phrase }
          )
        : t(
            "This article discusses a use of {{inn}} outside its authorised conditions of use {{countryPhrase}}.",
            { inn: inns, countryPhrase: phrase }
          );
    case "not_authorised":
      return several
        ? t(
            "This article discusses {{inns}}, drugs not authorised {{countryPhrase}}.",
            { inns, countryPhrase: phrase }
          )
        : t(
            "This article discusses {{inn}}, a drug not authorised {{countryPhrase}}.",
            { inn: inns, countryPhrase: phrase }
          );
    default:
      throw new Error(kind satisfies never);
  }
}

/**
 * The off-label warning of an article read in a room limited to some countries
 * (docs/off-label-warnings.md). The api sends the confirmed warnings of the
 * room's countries and nothing else, so this component only words them: no
 * rule about rooms or countries lives here. It groups them into one sentence
 * per country and kind. It sits above the AI summary, in
 * the Design System caution colours. Its sentences are `div`s: the phone body
 * styles every `p` under it with prose margins.
 *
 * `WEBAPP_OFF_LABEL_WARNING` gates the whole banner: off, it renders nothing.
 * The flag is read here, not on the two article pages that render it, so one
 * read covers the phone and the desktop. The read is reactive: a one-shot read
 * would race the GrowthBook payload (.knowledge/growthbook-oneshot-flag-race.md).
 */
export function ArticleSectionOffLabel({
  article,
}: {
  article: ArticlePageArticle;
}) {
  const { t, i18n } = useTranslation();
  const showWarning = useFeatureIsOn("WEBAPP_OFF_LABEL_WARNING");

  if (!showWarning) return null;
  if (article.offLabelWarnings.length === 0) return null;

  const listFormat = new Intl.ListFormat(i18n.language, {
    type: "conjunction",
  });

  return (
    <div
      role="note"
      data-gtm-section="off-label"
      className="flex w-full items-start gap-2 rounded-2xl border border-ds-black/6 bg-ds-clementine-50 p-4 font-dm-sans text-sm text-ds-clementine-700"
    >
      <IconAlertTriangle stroke={1.5} className="size-5 shrink-0" />
      <div className="flex flex-col gap-2">
        {groupWarnings(article.offLabelWarnings).map((group) => (
          <div
            key={`${group.country}-${group.kind}`}
            className="text-sm/snug font-normal"
          >
            {warningText(t, listFormat, group)}
          </div>
        ))}
      </div>
    </div>
  );
}
